<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    @save="onSave"
  >
    <template #layerId.input="data">
      <v-col cols="3">
        <data-input v-model="selectedDataSourceProps.settings.layer_id" v-bind="data" @change="layerIdChanged" />
      </v-col>
    </template>
    <template #countsAttribute.input="data">
      <v-col cols="3">
        <data-input
          v-model="selectedDataSourceProps.settings.attribute_name"
          v-bind="data"
          @change="settingsChanged = true"
        />
      </v-col>
    </template>
  </admin-wrapper>
</template>
<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'TransmissionEasementTwoSettings',
  components: { AdminWrapper, DataInput },
  mixins: [prepareAttributesFeatureCardMixin],
  computed: {
    additionalModuleName: get('admin/additionalModules@TRANSMISSION_EASEMENT_2_MODULE.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@transmission_easement_2_module_enabled'),
    layers: get('layers/layers'),
    dataSources: get('layers/metadata'),
    dataSourceSettingsName() {
      return 'transmission_easement_2_layer';
    },
    moduleEnabler() {
      return {
        name: 'transmission_easement_2_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      return [
        {
          name: 'layerId',
          translationPath: 'admin.transmissionEasementLayer',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.layersMapped,
        },
        {
          name: 'countsAttribute',
          translationPath: 'admin.transmissionEasementCountsAttribute',
          subtitle: 'admin.transmissionEasementCountsAttributeSubtitle',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.selectedLayerMappedAttributesArray,
        },
      ];
    },
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    layersFiltered() {
      return this.layersArray.filter(
        layer =>
          layer.type === 'features_layer' &&
          (layer.geometry_type === 'polygon' || layer.geometry_type === 'multipolygon') &&
          layer.layer_scope === 'core'
      );
    },
    layersMapped() {
      return this.layersFiltered.map(layer => {
        const { id, name } = layer;
        return { value: id, text: name };
      });
    },
    countsAttributesMapped() {
      return [];
    },
    selectedLayerMetadata() {
      return this.layers?.[this.selectedDataSourceProps.settings.layer_id] || undefined;
    },
    selectedLayerFormSchema() {
      return this.selectedLayerMetadata?.form_schema?.elements || [];
    },
    selectedLayerDataSourceName() {
      return this.selectedLayerMetadata?.data_source_name || '';
    },
    selectedLayerDataSourceMetadata() {
      return this.dataSources?.[this.selectedLayerDataSourceName] || undefined;
    },
    selectedLayerDataSourceAttributesSchema() {
      return this.selectedLayerDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedLayerAttributesArray() {
      return this.$_flattenStructure(
        this.getForm(this.selectedLayerFormSchema, this.selectedLayerDataSourceAttributesSchema, {
          returnDictRelationInfo: true,
        })
      );
    },
    selectedLayerAttributesFilteredArray() {
      return this.selectedLayerAttributesArray.filter(attribute => attribute.isDict);
    },
    selectedLayerMappedAttributesArray() {
      return this.selectedLayerAttributesFilteredArray.map(attribute => {
        const { attribute: value, label } = attribute;
        return { value, text: label };
      });
    },
  },
  data: () => ({
    isFormFetched: false,
    isSaving: false,
    selectedDataSourceProps: {
      settings: {
        layer_id: '',
        attribute_name: '',
      },
      datasource: '',
    },
    settingsChanged: false,
  }),
  methods: {
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    getDataSources: call('layers/getLayersMetadata'),
    getLayers: call('layers/getLayers'),
    getModulesMapping: call('admin/getModulesMapping'),
    async init() {
      await Promise.all([
        this.getModulesMapping(),
        this.getDataSources({ with_attributes_schema: true }),
        this.getLayers(),
      ]);
      if (this.isModuleEnabled) {
        await this.updateSelectedLayer();
      }
      this.isFormFetched = true;
    },
    async onSave() {
      if (!this.settingsChanged) {
        return;
      }
      const payload = { name: this.dataSourceSettingsName, settings: this.selectedDataSourceProps };
      try {
        await this.editDatasourceSettings(payload);
        this.settingsChanged = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch {
        //
      }
    },
    async updateSelectedLayer() {
      try {
        const r = await this.getDatasourceSettings(this.dataSourceSettingsName);
        const { datasource, settings } = r;
        this.selectedDataSourceProps = {
          datasource,
          settings,
        };
        this.settingsChanged = false;
      } catch {
        this.selectedDataSourceProps = {
          settings: {
            layer_id: '',
            attribute_name: '',
          },
          datasource: '',
        };
        this.settingsChanged = false;
      }
    },
    clearColumns() {
      this.selectedDataSourceProps.settings.attribute_name = '';
      this.settingsChanged = true;
    },
    layerIdChanged(value) {
      this.clearColumns();
      if (!value) return;
      this.selectedDataSourceProps.datasource = this.dataSources?.[this.selectedLayerDataSourceName]?.name || '';
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
